@if (isOpen()) {
  <div class="fixed inset-0 z-50">
    <!-- Backdrop -->
    <button
      type="button"
      class="absolute inset-0 backdrop-blur-[2px] bg-black/20"
      aria-label="Chiudi finestra"
      (click)="requestClose()"
    ></button>

    <!-- Dialog Content -->
    <div class="absolute inset-0 flex items-center justify-center p-3">
      <div
        class="relative flex flex-col w-full max-w-5xl max-h-[85vh] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="comparison-dialog-title"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-800"
        >
          <div class="flex items-center gap-3">
            <span
              class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30"
            >
              <svg
                class="h-4 w-4 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </span>
            <div>
              <h3
                id="comparison-dialog-title"
                class="text-base font-semibold text-gray-900 dark:text-white"
              >
                {{ title() }}
                @if (comparisons().length > 0) {
                  <span
                    class="ml-1.5 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  >
                    {{ comparisons().length }}
                  </span>
                }
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ description() }}
              </p>
            </div>
          </div>
          <button
            type="button"
            (click)="requestClose()"
            [disabled]="isLoading()"
            class="inline-flex h-7 w-7 items-center justify-center cursor-pointer rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Chiudi finestra"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Scrollable content -->
        <div class="flex-1 overflow-y-auto p-5">
          @if (isLoading()) {
            <div class="flex flex-col items-center justify-center py-12">
              <div
                class="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
              ></div>
              <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">Caricamento confronto...</p>
            </div>
          } @else if (viewMode() === 'single' && getSingleComparison(); as comparison) {
            <div class="space-y-5">
              <div
                class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p
                      class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    >
                      Tipologia confrontata
                    </p>
                    <h4 class="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {{ (comparison.doc_subtype || '' | comparisonLabel: 'document_type') || '-' }}
                    </h4>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {{ comparison.document_type | comparisonLabel: 'document_type' }}
                    </p>
                  </div>
                  @if (comparison.existed_in_previous_version === false) {
                    <span
                      class="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                    >
                      Nuovo nella versione attuale
                    </span>
                  } @else {
                    <span
                      class="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                    >
                      Presente nella versione precedente
                    </span>
                  }
                </div>
              </div>

              @if (getMeaningfulChanges(comparison.changed_fields).length > 0) {
                <div class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table class="w-full text-left text-[13px]">
                    <thead>
                      <tr
                        class="border-b border-gray-200 bg-gray-100 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                      >
                        <th scope="col" class="px-4 py-2.5 font-bold">Campo</th>
                        <th scope="col" class="px-4 py-2.5 font-bold">Valore Precedente</th>
                        <th scope="col" class="px-4 py-2.5 font-bold">Valore Attuale</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
                      @for (
                        field of getMeaningfulChanges(comparison.changed_fields);
                        track field.field_name
                      ) {
                        <tr class="bg-white transition-colors dark:bg-gray-900">
                          <td
                            class="max-w-65 px-4 py-3 font-medium text-gray-900 dark:text-gray-100"
                          >
                            <span class="wrap-break-word">{{
                              field.field_name
                                | comparisonLabel: 'changed_field' : comparison.document_type
                            }}</span>
                          </td>
                          <td class="max-w-55 px-4 py-3">
                            @if (field.previous_value) {
                              <span
                                class="inline-flex items-center gap-1.5 break-all rounded-md bg-red-50 px-2 py-1 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400"
                              >
                                {{
                                  displayValue(
                                    field.field_name,
                                    field.previous_value,
                                    comparison.document_type
                                  )
                                }}
                              </span>
                            } @else {
                              <span class="text-xs italic text-gray-400 dark:text-gray-500"
                                >— vuoto —</span
                              >
                            }
                          </td>
                          <td class="max-w-55 px-4 py-3">
                            @if (field.current_value) {
                              <span
                                class="inline-flex items-center gap-1.5 break-all rounded-md bg-green-50 px-2 py-1 text-xs text-green-700 dark:bg-green-900/20 dark:text-green-400"
                              >
                                {{
                                  displayValue(
                                    field.field_name,
                                    field.current_value,
                                    comparison.document_type
                                  )
                                }}
                              </span>
                            } @else {
                              <span class="text-xs italic text-gray-400 dark:text-gray-500"
                                >— vuoto —</span
                              >
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              } @else {
                <div
                  class="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-10 text-center dark:border-gray-700"
                >
                  <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                    Nessuna modifica rilevata
                  </h4>
                </div>
              }

              @if (getEmptyCount(comparison.changed_fields) > 0) {
                <p class="text-xs text-gray-400 dark:text-gray-500">
                  {{ getEmptyCount(comparison.changed_fields) }} campi invariati (entrambi vuoti)
                  non mostrati.
                </p>
              }
            </div>
          } @else if (comparisons().length > 0) {
            <div class="overflow-x-auto">
              <table
                class="w-full text-left text-[13px] text-gray-600 transition-colors dark:text-gray-300"
              >
                <thead>
                  <tr
                    class="border-b border-gray-100 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:border-gray-800 dark:text-gray-500"
                  >
                    @for (column of columns; track column.key) {
                      <th
                        scope="col"
                        class="pb-2 font-bold whitespace-nowrap"
                        [class.w-10]="column.key === 'expand'"
                      >
                        @if (column.key !== 'expand') {
                          {{ column.label }}
                        } @else {
                          &nbsp;
                        }
                      </th>
                    }
                  </tr>
                </thead>
                <tbody
                  class="relative divide-y divide-gray-50 transition-colors dark:divide-gray-800/50"
                >
                  @for (element of comparisons(); track $index) {
                    <tr
                      class="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      [class.bg-gray-50]="expandedElement() === element"
                      [class.dark:bg-gray-800/50]="expandedElement() === element"
                      (click)="toggleExpand(element)"
                    >
                      @for (column of columns; track column.key) {
                        @if (column.key === 'expand') {
                          <td class="py-2 px-2 align-middle">
                            <button
                              type="button"
                              (click)="toggleExpand(element); $event.stopPropagation()"
                              [attr.aria-expanded]="expandedElement() === element"
                              aria-label="Espandi riga"
                              class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                            >
                              @if (expandedElement() === element) {
                                <svg
                                  class="h-5 w-5 transition-transform"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  stroke="currentColor"
                                >
                                  <path
                                    d="M5 12l5-5 5 5"
                                    stroke-width="1.75"
                                    stroke-linecap="round"
                                  />
                                </svg>
                              } @else {
                                <svg
                                  class="h-5 w-5 transition-transform"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  stroke="currentColor"
                                >
                                  <path
                                    d="M5 8l5 5 5-5"
                                    stroke-width="1.75"
                                    stroke-linecap="round"
                                  />
                                </svg>
                              }
                            </button>
                          </td>
                        } @else {
                          <td
                            class="py-2 px-2"
                            [class.font-medium]="column.key === 'document_type'"
                            [class.text-gray-900]="column.key === 'document_type'"
                            [class.dark:text-gray-100]="column.key === 'document_type'"
                          >
                            @switch (column.key) {
                              @case ('document_type') {
                                {{
                                  element.document_type
                                    ? (element.document_type | comparisonLabel: 'document_type')
                                    : '-'
                                }}
                              }
                              @case ('doc_subtype') {
                                {{ element.doc_subtype || '-' }}
                              }
                              @case ('changes_count') {
                                <span
                                  class="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
                                >
                                  {{ element.changed_fields.length }} campi
                                </span>
                              }
                            }
                          </td>
                        }
                      }
                    </tr>

                    @if (expandedElement() === element) {
                      <tr>
                        <td [attr.colspan]="columns.length" class="border-none p-0">
                          <div
                            class="border-t border-gray-100 bg-gray-50 p-6 transition-colors dark:border-gray-800 dark:bg-gray-800/50"
                          >
                            <div class="mb-4 flex items-center gap-2">
                              <svg
                                class="h-4 w-4 text-gray-400 dark:text-gray-500"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V7.414A2 2 0 0017.414 6L14 2.586A2 2 0 0012.586 2H4z"
                                />
                              </svg>
                              <h3
                                class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                              >
                                Dettaglio Campi Modificati
                              </h3>
                            </div>

                            @if (getMeaningfulChanges(element.changed_fields).length > 0) {
                              <div
                                class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
                              >
                                <table class="w-full text-left text-[13px]">
                                  <thead>
                                    <tr
                                      class="border-b border-gray-200 bg-gray-100 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                    >
                                      <th scope="col" class="px-4 py-2.5 font-bold">Campo</th>
                                      <th scope="col" class="px-4 py-2.5 font-bold">
                                        Valore Precedente
                                      </th>
                                      <th scope="col" class="px-4 py-2.5 font-bold">
                                        Valore Attuale
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
                                    @for (
                                      field of getMeaningfulChanges(element.changed_fields);
                                      track field.field_name
                                    ) {
                                      <tr class="bg-white transition-colors dark:bg-gray-900">
                                        <td
                                          class="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 max-w-65"
                                        >
                                          <span class="wrap-break-word">{{
                                            field.field_name
                                              | comparisonLabel
                                                : 'changed_field'
                                                : element.document_type
                                          }}</span>
                                        </td>
                                        <td class="px-4 py-3 max-w-55">
                                          @if (field.previous_value) {
                                            <span
                                              class="inline-flex items-center gap-1.5 break-all rounded-md bg-red-50 px-2 py-1 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400"
                                            >
                                              <svg
                                                class="h-3 w-3 shrink-0"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                              >
                                                <path
                                                  fill-rule="evenodd"
                                                  d="M4 10a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H4.75A.75.75 0 014 10z"
                                                  clip-rule="evenodd"
                                                />
                                              </svg>
                                              {{
                                                displayValue(
                                                  field.field_name,
                                                  field.previous_value,
                                                  element.document_type
                                                )
                                              }}
                                            </span>
                                          } @else {
                                            <span
                                              class="text-xs italic text-gray-400 dark:text-gray-500"
                                              >— vuoto —</span
                                            >
                                          }
                                        </td>
                                        <td class="px-4 py-3 max-w-55">
                                          @if (field.current_value) {
                                            <span
                                              class="inline-flex items-center gap-1.5 break-all rounded-md bg-green-50 px-2 py-1 text-xs text-green-700 dark:bg-green-900/20 dark:text-green-400"
                                            >
                                              <svg
                                                class="h-3 w-3 shrink-0"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                              >
                                                <path
                                                  fill-rule="evenodd"
                                                  d="M10 3a.75.75 0 01.75.75v5.5h5.5a.75.75 0 010 1.5h-5.5v5.5a.75.75 0 01-1.5 0v-5.5h-5.5a.75.75 0 010-1.5h5.5v-5.5A.75.75 0 0110 3z"
                                                  clip-rule="evenodd"
                                                />
                                              </svg>
                                              {{
                                                displayValue(
                                                  field.field_name,
                                                  field.current_value,
                                                  element.document_type
                                                )
                                              }}
                                            </span>
                                          } @else {
                                            <span
                                              class="text-xs italic text-gray-400 dark:text-gray-500"
                                              >— vuoto —</span
                                            >
                                          }
                                        </td>
                                      </tr>
                                    }
                                  </tbody>
                                </table>
                              </div>
                            }

                            @if (getEmptyCount(element.changed_fields) > 0) {
                              <p class="mt-3 text-xs text-gray-400 dark:text-gray-500">
                                {{ getEmptyCount(element.changed_fields) }} campi invariati
                                (entrambi vuoti) non mostrati.
                              </p>
                            }
                          </div>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <svg
                class="mx-auto h-12 w-12 text-gray-200 dark:text-gray-800"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                Nessuna modifica rilevata
              </h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Questa versione è identica alla precedente.
              </p>
            </div>
          }
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-5 py-4 dark:border-gray-800 dark:bg-gray-900/50"
        >
          <button
            type="button"
            (click)="requestClose()"
            class="inline-flex cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-95 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  </div>
}
